/*
 * fyre-companion-flyout.js — "Complete Your Vehicle" companion configurator flyout.
 *
 * ADDITIVE / zero-touch: does NOT modify fyre-resolve.js. Intercepts the companion card's CONFIGURE
 * click (the [data-companion-cta] <a>, whose href renderCompanion already sets to the paired rear
 * umbrella handle), preventDefaults the navigate, and opens a right-side drawer (.pdp-cf) hosting a
 * mini-PDP of the rear kit: image + name + four <select>s (COLOR / DIAMETER / DISC TYPE / BRAKE PAD)
 * + ADD-ON COST + dark ATC. Runs its OWN isolated resolve + cart-add so it can never touch the front
 * PDP's global option selectors or shared cart form (front-corruption risk class = avoided by construction).
 *
 * DATA SOURCING: rear config comes from a lightweight Storefront REAR_ID_QUERY (handle -> umbrella id +
 * builder collection + image + title), PREFETCHED the moment the front resolver pairs a rear
 * (fyre:resolved event / CTA href observer) so the CONFIGURE click renders instantly — no "Loading…".
 * Option universes come from the data the resolver already loads: DIAMETER from the fitment graph
 * (backings that fit the LOCKED vehicle), COLOR/DISC TYPE from the chosen backing's variant matrix,
 * BRAKE PAD from its content.pad_products refs.
 *
 * CART SAFETY: epoch/isStale guards on every async continuation, buyer-aware price overlay,
 * diameter-aware variant match, pre-cart cross-check, pad rides as a line-item property, Custom Color
 * excluded, isolated atomic REAR-ONLY /cart/add.js.
 * ATC success flashes ADDED ✓, slides this drawer out, THEN opens the mini-cart as it clears (z 9999 > 9998).
 */
(function () {
  'use strict';

  // Only builder-umbrella PDPs carry the companion card host.
  var companionHost = document.querySelector('[data-pdp-companion]');
  if (!companionHost) return;

  // ── Shared utils ─────────────────────────────────────────────────────────────────────────────
  // Front option names/values -> backing option names/values. Only needed when reading the FRONT
  // page's radios (front caliper-color default); our own selects use backing names/values natively.
  var NAME_TO_BACKING = { 'finish': 'Caliper Color', 'color': 'Caliper Color', 'colour': 'Caliper Color', 'diameter': 'Disc Diameter', 'disc type': 'Disc Type', 'disc face': 'Disc Type' };
  var VALUE_TO_BACKING = { 'Custom Color': 'Custom' };
  function normDia(s) { return s ? String(s).replace(/[^0-9.]/g, '') : ''; }
  function numericId(gid) { var m = /(\d+)$/.exec(String(gid)); return m ? m[1] : ''; }
  function lockedVehicleHandle() {
    var v = null;
    try { v = window.FitmentStorage ? window.FitmentStorage.get() : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null'); } catch (e) { v = null; }
    return v && (v.id || v.handle) ? (v.id || v.handle) : '';
  }
  function vehicleDisplay() {
    try { var v = window.FitmentStorage ? window.FitmentStorage.get() : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null'); return v ? (v.display || [v.year, v.make, v.model, v.submodel].filter(Boolean).join(' ')) : ''; } catch (e) { return ''; }
  }
  function money(p) { try { return Number(p.amount).toLocaleString('en-US', { style: 'currency', currency: p.currencyCode || 'USD' }); } catch (e) { return '$' + (p && p.amount); } }
  function padCompound(t) { return String(t || '').split('| ').pop().split(' Compound')[0].trim(); } // "…| Brembo FM1000 Compound, 18mm" -> "Brembo FM1000"
  function escHtml(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : s); return d.innerHTML; }
  function escAttr(s) { return String(s == null ? '' : s).replace(/"/g, '&quot;'); }
  function sizedUrl(url, w) { if (!url) return url; return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; }
  function noop() {}

  // ── Storefront config (read from the FRONT page's [data-fyre-resolve] — fyre-resolve.js untouched) ──
  var frontCfg = null;
  function getFrontCfg() {
    if (frontCfg) return frontCfg;
    var el = document.querySelector('[data-fyre-resolve]');
    if (!el || !el.dataset.sfToken) return null; // no resolver on this page -> flyout can't query; CONFIGURE degrades to navigate
    frontCfg = {
      token: el.dataset.sfToken,
      api: '/api/' + (el.dataset.sfVersion || '2026-04') + '/graphql.json',
      isB2B: !!(el.dataset.buyer || ''),
      // Distinct from isB2B above: that one asks "overlay dealer prices?" (needs a chosen location), this one asks
      // "may this buyer see B2B-only SKUs?". A dealer with no location selected is still a B2B buyer.
      isB2BCustomer: (el.dataset.b2b || (document.body && document.body.dataset.b2b)) === 'true'
    };
    return frontCfg;
  }
  function sf(query, vars) {
    var cfg = getFrontCfg();
    if (!cfg) return Promise.reject(new Error('no storefront config'));
    return fetch(cfg.api, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token }, body: JSON.stringify({ query: query, variables: vars || {} }) })
      .then(function (r) { return r.json(); }).then(function (j) { if (j.errors) throw new Error(JSON.stringify(j.errors)); return j.data; });
  }

  // ── Queries ──────────────────────────────────────────────────────────────────────────────────
  // Lightweight rear identity by handle — umbrella id (never-cart guard), builder collection
  // (the fitment graph source), image + title for the drawer head.
  var REAR_ID_QUERY =
    'query($handle: String!) { product(handle: $handle) { id title featuredImage { url }' +
    ' bc: metafield(namespace: "custom", key: "builder_collections") { value } } }';
  // Minimal matrix query: resolution + price + the REQUIRED pad compounds — no spec/replacement metafields.
  var REAR_MATRIX_QUERY =
    'query($id: ID!) @inContext(country: US) { product(id: $id) { handle' +
    ' pads: metafield(namespace: "content", key: "pad_products") { references(first: 20) { nodes { ... on Product {' +
    '   title variants(first: 1) { nodes { id sku availableForSale } } } } } }' +
    // RT Product Feed — the same variant/product pair fyre-resolve.js reads, so the flyout gates on one predicate.
    ' pFeed: metafield(namespace: "custom", key: "rt_product_feed") { value }' +
    ' variants(first: 100) { nodes {' +
    '   id sku availableForSale image { url } price { amount currencyCode } selectedOptions { name value }' +
    '   vFeed: metafield(namespace: "custom", key: "rt_feed") { value } } } } }';
  // Graph: rear collection -> backings, each with disc_diameter + fitment.groups -> vehicle handles.
  var REAR_GRAPH_QUERY =
    'query($id: ID!, $cursor: String) { collection(id: $id) { products(first: 250, after: $cursor) {' +
    ' pageInfo { hasNextPage endCursor } nodes { id dia: metafield(namespace: "specs", key: "disc_diameter") { value }' +
    ' fg: metafield(namespace: "fitment", key: "groups") { references(first: 128) { nodes { ... on Metaobject {' +
    ' vehicles: field(key: "vehicles") { references(first: 250) { nodes { ... on Metaobject { handle } } } } } } } } } } } }';

  // ── Module caches (page-session; PURE DATA, no DOM writes — every DOM write downstream stays
  // epoch-guarded in the resolver, so prefetch can safely warm these with no flyout session open) ──
  var infoCache = {};      // rear handle -> Promise<{umbrella,title,image,coll}|null>
  var graphCache = {};     // COLL_NUM -> graph (resolved)
  var graphInflight = {};  // COLL_NUM -> Promise<graph> (dedup concurrent prefetch + open)
  var matrixCache = {};    // backing GID -> structural matrix
  var handleCache = {};    // backing GID -> product handle (buyer-price endpoint key)
  var padsCache = {};      // backing GID -> [{title,compound,sku,variantId,available}]
  var pricedCache = {};    // backing GID -> buyer-priced matrix (buyer is fixed for the page life — data-buyer is server-rendered)
  var matrixInflight = {}; // backing GID -> Promise<priced matrix>

  function parseBuilderColl(raw) {
    if (!raw) return '';
    try { var arr = JSON.parse(raw); if (arr && arr.length) return numericId(arr[0]); } catch (e) { /* not JSON -> raw GID */ }
    return numericId(raw);
  }

  function fetchGraph(coll) {
    if (graphCache[coll]) return Promise.resolve(graphCache[coll]);
    if (graphInflight[coll]) return graphInflight[coll];
    var acc = [];
    function page(cursor) {
      return sf(REAR_GRAPH_QUERY, { id: 'gid://shopify/Collection/' + coll, cursor: cursor }).then(function (d) {
        var conn = d && d.collection && d.collection.products;
        acc = acc.concat((conn && conn.nodes) || []);
        var pi = conn && conn.pageInfo;
        return (pi && pi.hasNextPage) ? page(pi.endCursor) : acc;
      });
    }
    graphInflight[coll] = page(null).then(function (nodes) {
      var graph = nodes.map(function (p) {
        var vehicles = {}, groups = (p.fg && p.fg.references && p.fg.references.nodes) || [];
        groups.forEach(function (g) { ((g.vehicles && g.vehicles.references && g.vehicles.references.nodes) || []).forEach(function (v) { vehicles[v.handle] = true; }); });
        return { id: p.id, diameter: normDia(p.dia && p.dia.value), vehicles: vehicles };
      });
      graphCache[coll] = graph;
      delete graphInflight[coll];
      return graph;
    }).catch(function (e) { delete graphInflight[coll]; throw e; });
    return graphInflight[coll];
  }

  // buyer-aware price for a logged-in dealer only (Storefront is buyer-blind); degrades to retail on any miss.
  function overlayBuyerPrices(handle, matrix) {
    var cfg = getFrontCfg();
    if (!cfg || !cfg.isB2B || !handle) return Promise.resolve(matrix);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var vs = p && p.variants; if (!vs) return matrix;
        var byId = {}; vs.forEach(function (v) { byId[String(v.id)] = v.price; });
        return matrix.map(function (m) { var c = byId[numericId(m.id)]; if (c == null) return m; var o = {}; for (var k in m) o[k] = m[k]; o.price = { amount: (c / 100).toFixed(2), currencyCode: (m.price && m.price.currencyCode) || 'USD' }; return o; });
      }).catch(function () { return matrix; });
  }

  function loadMatrix(backing) {
    if (pricedCache[backing.id]) return Promise.resolve(pricedCache[backing.id]);
    if (matrixInflight[backing.id]) return matrixInflight[backing.id];
    var structural = matrixCache[backing.id]
      ? Promise.resolve(matrixCache[backing.id])
      : sf(REAR_MATRIX_QUERY, { id: backing.id }).then(function (d) {
          var prod = d.product || {}, nodes = (prod.variants && prod.variants.nodes) || [];
          // RT Product Feed gate — drop the SKUs this buyer may not see BEFORE the matrix exists, so the rear
          // selector cannot offer one and ATC below cannot post one. Nothing else gates this path: these variants
          // come from the Storefront API and are never Liquid-rendered. Fail CLOSED for retail if the predicate
          // script is missing, matching fyre-resolve.js.
          var rtCfg = getFrontCfg(), rtB2B = !!(rtCfg && rtCfg.isB2BCustomer), rtPFeed = prod.pFeed && prod.pFeed.value;
          nodes = nodes.filter(function (n) {
            if (!window.FyreRtFeed) return rtB2B;
            return window.FyreRtFeed.visible(n.vFeed && n.vFeed.value, rtPFeed, rtB2B);
          });
          var m = nodes.map(function (n) { var opts = {}; (n.selectedOptions || []).forEach(function (o) { opts[o.name] = o.value; }); return { id: n.id, sku: n.sku, available: n.availableForSale, price: n.price, image: n.image && n.image.url, opts: opts }; });
          matrixCache[backing.id] = m; handleCache[backing.id] = prod.handle || '';
          padsCache[backing.id] = ((prod.pads && prod.pads.references && prod.pads.references.nodes) || []).map(function (p) {
            var pv = (p.variants && p.variants.nodes && p.variants.nodes[0]) || null;
            return pv ? { title: p.title, compound: padCompound(p.title), sku: pv.sku, variantId: numericId(pv.id), available: !!pv.availableForSale } : null;
          }).filter(Boolean);
          return m;
        });
    matrixInflight[backing.id] = structural
      .then(function (m) { return overlayBuyerPrices(handleCache[backing.id], m); })
      .then(function (pr) { pricedCache[backing.id] = pr; delete matrixInflight[backing.id]; return pr; })
      .catch(function (e) { delete matrixInflight[backing.id]; throw e; });
    return matrixInflight[backing.id];
  }

  // The backing refresh() would pick for the locked vehicle with no user dia pick yet: first (numerically
  // sorted) fitting diameter, first matching backing. MUST mirror refresh()'s default so the prefetch-warmed
  // matrix is the one the open actually uses.
  function defaultBackingFor(graph, vh) {
    var fitting = graph.filter(function (b) { return b.vehicles[vh]; });
    if (!fitting.length) return null;
    var dias = {}; fitting.forEach(function (b) { if (b.diameter) dias[b.diameter] = true; });
    var keys = Object.keys(dias).sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
    var first = keys[0] || '';
    var match = fitting.filter(function (b) { return !b.diameter || !first || b.diameter === first; });
    return match[0] || null;
  }

  // ── Prefetch (kills the "Loading…" state) — warm identity + graph + the default backing's matrix the
  // moment the front resolver pairs a rear, so the CONFIGURE click renders from cache. Dedup via infoCache. ──
  function prefetchRear(handle) {
    if (!handle) return null;
    if (infoCache[handle]) return infoCache[handle];
    if (!getFrontCfg()) return null;
    infoCache[handle] = sf(REAR_ID_QUERY, { handle: handle }).then(function (d) {
      var p = d && d.product;
      if (!p) return null; // rear not resolvable via Storefront -> openFor falls back to navigate
      var info = { umbrella: numericId(p.id), title: p.title || '', image: (p.featuredImage && p.featuredImage.url) || '', coll: parseBuilderColl(p.bc && p.bc.value) };
      if (info.coll) {
        // best-effort warm: graph, then the matrix the open would load first. Errors swallowed — the
        // open path re-runs fetchGraph/loadMatrix itself with real error handling.
        fetchGraph(info.coll).then(function (graph) {
          var vh = lockedVehicleHandle(); if (!vh) return;
          var b = defaultBackingFor(graph, vh);
          if (b) loadMatrix(b).catch(noop);
        }).catch(noop);
      }
      return info;
    }).catch(function (e) { delete infoCache[handle]; throw e; }); // failed identity is retryable on next trigger/open
    return infoCache[handle];
  }
  function ctaHandle() {
    var cta = companionHost.querySelector('[data-companion-cta]');
    var href = (cta && cta.getAttribute('href')) || '';
    return /^\/products\//.test(href) ? href.replace(/^\/products\//, '').split('?')[0].split('#')[0] : '';
  }
  function triggerPrefetch() { var h = ctaHandle(); if (h) { var p = prefetchRear(h); if (p) p.catch(noop); } }

  // ── Nice-to-have: default the rear COLOR to the FRONT's selected caliper color ("match your vehicle"). ──
  function frontColorDefault() {
    var radios = document.querySelectorAll('[data-pdp-option-radio]');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      if (!r.checked) continue;
      var m = /^options\[(.+)\]$/.exec(r.getAttribute('name') || '');
      if (!m) continue;
      if ((NAME_TO_BACKING[m[1].toLowerCase()] || m[1]) !== 'Caliper Color') continue;
      return VALUE_TO_BACKING[r.value] || r.value; // 'Custom' never matches the rear select (stripped) -> falls to first
    }
    return '';
  }

  // ── Scoped rear resolver ─────────────────────────────────────────────────────────────────────
  // A SLICE of fyre-resolve.js's resolve math bound to the flyout's OWN <select>s ([data-cf-opt]) so it
  // can never touch the front instance. Resolves the rear umbrella against the LOCKED vehicle to a real
  // backing +K variant + buyer-aware add-on cost. NO cart-add outside onAtc (gated): it records
  // data-cf-resolved-sku/-id for the isolated-ATC pass to consume.
  function makeResolver(container, cfg, isStale, onAdded) {
    var COLL = cfg.coll, UMBRELLA = cfg.umbrella; // rear umbrella product id — the placeholder we must NEVER cart
    var colorSel = container.querySelector('[data-cf-opt="Caliper Color"]');
    var diaSel = container.querySelector('[data-cf-opt="Disc Diameter"]');
    var typeSel = container.querySelector('[data-cf-opt="Disc Type"]');
    var padSel = container.querySelector('[data-cf-pad]');
    var imgEl = container.querySelector('[data-cf-img]');
    var costEl = container.querySelector('[data-cf-cost]'), msgEl = container.querySelector('[data-cf-msg]'), atcEl = container.querySelector('[data-cf-atc]');
    var activeBacking = null, seq = 0, lastResolved = null, added = false;

    function optSelects() { return [colorSel, diaSel, typeSel].filter(Boolean); }
    function picks() {
      var out = {};
      optSelects().forEach(function (s) { if (s.value) out[s.getAttribute('data-cf-opt')] = s.value; });
      return out;
    }
    function candidates(graph, vh) { return (!graph || !vh) ? [] : graph.filter(function (b) { return b.vehicles[vh]; }); }
    function setCost(t) { if (costEl) costEl.textContent = t; }
    function setMsg(t) { if (msgEl) msgEl.textContent = t || ''; }
    function setImg(url) { if (imgEl && url) { imgEl.src = sizedUrl(url, 720); imgEl.style.display = ''; } }
    // WHY: row visibility via inline style.display, NOT the hidden attr — author display rules defeat [hidden].
    function showRow(sel, show) { var row = sel && sel.closest('[data-cf-row]'); if (row) row.style.display = show ? '' : 'none'; }

    // Rebuild a select's options in place. Keeps the current pick when still offered, else the preferred
    // value, else auto-picks the first. Auto-correcting in place dispatches no event — setting
    // .value programmatically fires no 'change', so refresh never recurses.
    function setSelectOptions(sel, opts, placeholder, preferred) {
      if (!sel) return;
      var cur = sel.value;
      var html = '<option value="" disabled>' + escHtml(placeholder) + '</option>';
      opts.forEach(function (o) { html += '<option value="' + escAttr(o.value) + '">' + escHtml(o.label || o.value) + '</option>'; });
      sel.innerHTML = html;
      var has = function (v) { return v && opts.some(function (o) { return o.value === v; }); };
      sel.value = has(cur) ? cur : (has(preferred) ? preferred : (opts.length ? opts[0].value : ''));
      sel.disabled = !opts.length;
    }
    function toOpts(values) { return values.map(function (v) { return { value: v, label: v }; }); }

    function clearResolved() { lastResolved = null; container.removeAttribute('data-cf-resolved-sku'); container.removeAttribute('data-cf-resolved-id'); }
    // unresolvable state: reset selects + pads, disable ATC, show why. Keeps the flyout honest (never a stale add-able state).
    function bail(msg) {
      activeBacking = null;
      optSelects().forEach(function (s) { setSelectOptions(s, [], s.getAttribute('data-cf-placeholder') || 'SELECT'); });
      renderPadOptions([]);
      disableAtc('UNAVAILABLE');
      setCost('—'); setMsg(msg);
    }

    // WHY: 'Disc Diameter' is a PRODUCT axis only when the backing is product-split (backing.diameter
    // truthy) — skip it there. When backing.diameter is falsy the diameter lives on the VARIANT and MUST be
    // matched, or a wrong-diameter SKU resolves and carts. Shared by resolveFromMatrix + the onAtc cross-check.
    function variantMatchesPicks(v, backing) {
      var p = picks();
      return Object.keys(p).every(function (bn) {
        if (bn === 'Disc Diameter') return (backing && backing.diameter) ? true : (!(bn in v.opts) ? true : normDia(v.opts[bn]) === normDia(p[bn])); // WHY: mirror front resolver — a variant-grain backing whose variants carry NO Disc Diameter option must fail-OPEN (skip), not fail-closed brick.
        return v.opts[bn] === p[bn];
      });
    }
    // match Color x DiscType (+ Diameter on the VARIANT when the backing isn't product-split).
    function resolveFromMatrix(matrix) {
      var hit = matrix.filter(function (v) { return variantMatchesPicks(v, activeBacking); });
      return { variant: hit.length === 1 ? hit[0] : null, count: hit.length };
    }

    // vehicle is LOCKED; Diameter picks the backing (product axis), Color/DiscType resolve within it.
    function refresh() {
      if (isStale()) return; // WHY: resolver epoch superseded (close/reopen) — never write into the REUSED bodyEl
      if (added) return; // WHY (dup-add guard): once this session has committed an add, FREEZE it. The ~450ms ADDED-✓ dwell before close() is interactive, so a select change here must NOT re-arm ATC into a second /cart/add.js.
      clearResolved();
      var graph = graphCache[COLL];
      if (!graph) { setMsg(''); setCost('—'); disableAtc('CHECKING…'); return; } // graph fetch kicked off below re-runs refresh on landing
      var vh = lockedVehicleHandle();
      if (!vh) return bail('Select your vehicle on the product first');
      var fitting = candidates(graph, vh);
      if (!fitting.length) return bail('This rear kit does not fit your vehicle');
      // DIAMETER options = the fitting backings' diameters (product axis). Sorted ascending — MUST match
      // defaultBackingFor()'s ordering or the prefetch warms the wrong matrix.
      var fitDias = {}; fitting.forEach(function (b) { if (b.diameter) fitDias[b.diameter] = true; });
      var diaKeys = Object.keys(fitDias).sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
      if (diaKeys.length) {
        setSelectOptions(diaSel, diaKeys.map(function (d) { return { value: d, label: d + 'mm' }; }), 'SELECT DIAMETER');
        showRow(diaSel, true);
      }
      // (diameter-less family: leave the DIAMETER row for the matrix step — variant-grain backings may
      // carry it on the variant; if neither, the row hides and the match fails OPEN — see variantMatchesPicks.)
      var diaPick = normDia(picks()['Disc Diameter'] || '');
      var match = fitting.filter(function (b) { return !b.diameter || !diaPick || b.diameter === diaPick; });
      if (!match.length) return bail('Select a diameter');
      activeBacking = match[0];
      var mySeq = ++seq;
      setMsg(''); setCost('—'); disableAtc('CHECKING…');
      loadMatrix(activeBacking).then(function (matrix) {
        if (isStale() || mySeq !== seq) return; // superseded by a newer pick OR a newer flyout session — no stale data-cf-resolved-id writes
        // option universes this backing actually offers (COLOR/DISC TYPE live on the variants)
        var uni = {}; matrix.forEach(function (v) { Object.keys(v.opts).forEach(function (bn) { (uni[bn] = uni[bn] || {})[v.opts[bn]] = true; }); });
        // WHY: Custom/Custom Color is a no-SKU un-fulfillable variant in the flyout — excluded from the COLOR select entirely.
        var colors = Object.keys(uni['Caliper Color'] || {}).filter(function (c) { return !/^custom/i.test(c); });
        setSelectOptions(colorSel, toOpts(colors), 'SELECT COLOR', frontColorDefault());
        setSelectOptions(typeSel, toOpts(Object.keys(uni['Disc Type'] || {})), 'SELECT DISC TYPE');
        if (!activeBacking.diameter) {
          // variant-grain backing: diameter is a VARIANT axis — offer it from the matrix so
          // variantMatchesPicks can enforce it. No diameter anywhere -> hide the row (fail-OPEN).
          var vDias = Object.keys(uni['Disc Diameter'] || {});
          if (vDias.length) { setSelectOptions(diaSel, toOpts(vDias), 'SELECT DIAMETER'); showRow(diaSel, true); }
          else showRow(diaSel, false);
        }
        renderPadOptions(padsCache[activeBacking.id] || []); // REQUIRED pad compounds for this backing
        var res = resolveFromMatrix(matrix), v = res.variant;
        if (!v) { lastResolved = null; setMsg(res.count > 1 ? 'Choose options' : 'Not available for this combination'); return updateAtc(); }
        // record the resolved rear backing for the isolated ATC — NEVER cart the umbrella placeholder.
        container.setAttribute('data-cf-resolved-sku', v.sku || '');
        lastResolved = { variant: v, backing: activeBacking, picks: picks() };
        if (v.image) setImg(v.image); // config-correct visual (color/disc-specific variant image)
        if (!v.available) { container.removeAttribute('data-cf-resolved-id'); setMsg('Out of stock'); return updateAtc(); }
        container.setAttribute('data-cf-resolved-id', numericId(v.id)); // set ONLY when purchasable
        setCost('+' + (v.price ? money(v.price) : '—'));
        updateAtc();
      }).catch(function () { if (!isStale() && mySeq === seq) { lastResolved = null; setCost('—'); setMsg('Rear configurator unavailable'); disableAtc('UNAVAILABLE'); } }); // WHY: a stale epoch never touches the new session's footer
    }

    // ── Required pad select — the pad rides as a PROPERTY on the rear kit line (kit BOM includes the
    // pad set; mirrors the front builder — never a separate paid line = no double-charge). ATC blocks until chosen.
    function renderPadOptions(pads) {
      if (!padSel) return;
      showRow(padSel, !!(pads && pads.length));
      if (!pads || !pads.length) { padSel.innerHTML = ''; padSel.disabled = true; return; } // no pads defined -> row hidden, ATC gate skips (updateAtc runs at the end of every refresh path)
      var prev = selectedPad();
      var html = '<option value="" disabled>SELECT BRAKE PAD</option>';
      pads.forEach(function (p) {
        html += '<option value="' + escAttr(p.sku) + '" data-cf-pad-vid="' + escAttr(p.variantId) + '" data-cf-pad-compound="' + escAttr(p.compound) + '"' + (p.available ? '' : ' disabled') + '>' + escHtml(p.compound || p.title) + '</option>';
      });
      padSel.innerHTML = html;
      // WHY: fail-closed — never auto-select/restore an UNAVAILABLE pad. Auto-default = previous pick
      // if still available, else the FIRST AVAILABLE pad (pre-matched rear intent); none available -> ''.
      var target = '';
      if (prev) pads.forEach(function (p) { if (!target && p.available && p.sku === prev.sku) target = p.sku; });
      if (!target) pads.forEach(function (p) { if (!target && p.available) target = p.sku; });
      padSel.value = target;
      padSel.disabled = !pads.some(function (p) { return p.available; });
    }
    function selectedPad() {
      if (!padSel || padSel.selectedIndex < 0) return null;
      var o = padSel.options[padSel.selectedIndex];
      // WHY: a selected-but-disabled (unavailable) pad — or the placeholder — must never satisfy the gate.
      if (!o || !o.value || o.disabled) return null;
      return { sku: o.value, variantId: o.getAttribute('data-cf-pad-vid'), compound: o.getAttribute('data-cf-pad-compound') };
    }
    function padDefined() { return !!(padSel && padSel.options.length > 1); } // options beyond the placeholder = this kit defines pads
    function anySelectablePad() {
      if (!padSel) return false;
      for (var i = 0; i < padSel.options.length; i++) { var o = padSel.options[i]; if (o.value && !o.disabled) return true; }
      return false;
    }

    function disableAtc(label) { if (atcEl) { atcEl.disabled = true; atcEl.textContent = label || 'UNAVAILABLE'; } }
    // ATC enabled ONLY when a purchasable backing +K is resolved AND (if the kit has pad choices) a pad is chosen.
    function updateAtc() {
      if (!atcEl) return;
      if (added) { atcEl.disabled = true; return; } // WHY (dup-add guard): session already added — never re-enable ATC (pad-change or any re-entry) during the pre-close dwell.
      var ready = lastResolved && lastResolved.variant && lastResolved.variant.sku && lastResolved.variant.available && container.getAttribute('data-cf-resolved-id'); // WHY: a no-SKU variant is un-fulfillable — onAtc would dead-end, so never enable for it
      if (!ready) return disableAtc(lastResolved && lastResolved.variant && !lastResolved.variant.available ? 'OUT OF STOCK' : 'SELECT OPTIONS');
      if (padDefined() && !selectedPad()) {
        // WHY: fail-closed — a kit that DEFINES pads but has none selectable (all unavailable) must block ATC, not skip the gate.
        return disableAtc(anySelectablePad() ? 'SELECT A BRAKE PAD' : 'PAD UNAVAILABLE');
      }
      atcEl.disabled = false;
      atcEl.innerHTML = '<i class="fa-regular fa-plus" aria-hidden="true"></i> ADD TO CART';
    }

    // ── ATC (cart-safety) — PRE-CART CROSS-CHECK, then an isolated atomic /cart/add.js of the REAR
    // backing +K only. Any inconsistency fails closed (never carts a placeholder). Mini-cart opens via the
    // theme's 'atc:added' event; the flyout then closes. Adds the REAR backing only — the pad rides as a
    // line-item property, not its own line.
    function onAtc() {
      var lr = lastResolved, v = lr && lr.variant, vid = container.getAttribute('data-cf-resolved-id');
      if (!v || !v.sku || !v.available || !vid || numericId(v.id) !== vid) return atcFail('Please reconfigure');
      var p = picks(), optsMatch = variantMatchesPicks(v, lr.backing); // WHY: same diameter-aware match as resolve — never cart a variant whose diameter differs from the pick on variant-grain backings
      if (!optsMatch) return atcFail('Please reconfigure');
      if (!lr.backing || (UMBRELLA && numericId(lr.backing.id) === UMBRELLA)) return atcFail('Please reconfigure'); // never the umbrella
      // WHY: re-read the locked vehicle at cart time — a cross-tab change can land between resolve and
      // click; the resolved backing must still fit the CURRENT vehicle or we'd cart a mis-fit rear.
      var vh = lockedVehicleHandle();
      if (!vh || !lr.backing.vehicles[vh]) return atcFail('Please reconfigure');
      var pad = selectedPad(); // WHY: selectedPad ignores disabled options — re-asserts an AVAILABLE pad at cart time
      if (padDefined() && !pad) return atcFail('Select a brake pad');
      var props = {
        'Caliper Color': p['Caliper Color'] || '',
        // WHY: assert the RESOLVED variant's real diameter — the props must never claim a diameter the variant doesn't have.
        'Disc Diameter': (lr.variant.opts['Disc Diameter'] || (lr.backing.diameter ? lr.backing.diameter + 'mm' : (p['Disc Diameter'] || ''))),
        'Disc Face': p['Disc Type'] || '',
        'Fits': vehicleDisplay()
      };
      if (pad) { if (pad.compound) props['Brake Pad'] = pad.compound; if (pad.sku) props['_pad_sku'] = pad.sku; }
      atcEl.disabled = true; atcEl.textContent = 'ADDING…';
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1, properties: props }] }) })
        .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'Could not add to cart'); }); return res.json(); })
        .then(function (data) {
          if (isStale()) return; // WHY: epoch sweep — a close / cross-tab vehicle change during the ~300ms POST must not fire a stale success on a torn-down session
          added = true; // latch: this session has committed an add — freeze the configurator (refresh/updateAtc guards) so the interactive pre-close dwell can't re-arm a duplicate add.
          // Do NOT open the mini-cart here: at t=0 the flyout is still up, so the two right-side drawers
          // would stack mid-transition. onAdded owns the sequence: flash ADDED ✓ → slide THIS drawer out
          // → THEN open the cart as it clears.
          atcEl.textContent = 'ADDED ✓';
          if (onAdded) onAdded(data && data.items && data.items[0] ? data.items[0].product_id : null);
        })
        .catch(function (e) { if (!isStale()) atcFail((e && e.message) || 'Could not add to cart'); });
    }
    function atcFail(msg) { if (!atcEl) return; atcEl.disabled = true; atcEl.textContent = msg; setTimeout(function () { if (!isStale()) updateAtc(); }, 2500); }

    optSelects().forEach(function (s) { s.addEventListener('change', function () { setTimeout(refresh, 0); }); });
    if (padSel) padSel.addEventListener('change', updateAtc);
    if (atcEl) atcEl.addEventListener('click', onAtc);
    disableAtc('CHECKING…');
    // usually a cache hit (prefetched on fyre:resolved) -> refresh renders synchronously after this microtask.
    fetchGraph(COLL).then(function () { if (isStale()) return; refresh(); }).catch(function () { if (isStale()) return; setCost('—'); setMsg('Rear configurator unavailable'); disableAtc('UNAVAILABLE'); }); // WHY: a late graph fetch from a prior open must not drive the reused DOM
    return { refresh: refresh };
  }

  // ── Flyout shell ─────────────────────────────────────────────────────────────────────────────
  var FyreCompanionFlyout = {
    el: null, bodyEl: null, isOpen: false, currentHandle: null, _seq: 0, _resolver: null,

    init: function () {
      // Own container (.pdp-cf, z 9998 — BELOW the mini-cart's 9999 so the cart opens on top of it).
      // SEPARATE from the tooltip FyreFlyout singleton — a tooltip click must never clobber the configurator.
      var html =
        '<div class="pdp-cf" data-companion-flyout>' +
          '<div class="pdp-cf__overlay" data-cf-close></div>' +
          '<div class="pdp-cf__panel" role="dialog" aria-modal="true" aria-label="Complete your vehicle">' +
            '<div class="pdp-cf__header">' +
              '<p class="pdp-cf__heading">COMPLETE YOUR VEHICLE</p>' +
              '<button type="button" class="pdp-cf__close" data-cf-close aria-label="Close">' +
                '<i class="fa-regular fa-times" aria-hidden="true"></i>' +
              '</button>' +
            '</div>' +
            '<div class="pdp-cf__body" data-cf-body></div>' +
          '</div>' +
        '</div>';
      document.body.insertAdjacentHTML('beforeend', html);
      this.el = document.querySelector('[data-companion-flyout]');
      this.bodyEl = this.el.querySelector('[data-cf-body]');

      var self = this;
      this.el.querySelectorAll('[data-cf-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.close(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });
      // Locked vehicle changed while the flyout is open → close it. The rear was resolved against the OLD
      // vehicle; reopening re-derives from the new front resolve. Prevents carting a stale-vehicle rear.
      window.addEventListener('fitment:changed', function () { if (self.isOpen) self.close(); });
      // WHY: 'fitment:changed' fires same-tab only — a vehicle change in ANOTHER tab arrives here as a
      // 'storage' event (key === null means localStorage.clear()). The open flyout resolved against the OLD
      // vehicle, so close it (same recovery as the same-tab handler above).
      window.addEventListener('storage', function (e) {
        if (e.key !== null && e.key !== 'fitment_vehicle') return;
        if (self.isOpen) self.close();
      });

      // Prefetch triggers: the front resolver dispatches 'fyre:resolved' on every resolve and sets
      // the CTA href in renderCompanion. The rAF defers past renderCompanion on the OK path; the observer
      // catches the OOS path (failClosed fires notifyResolved(null) BEFORE renderCompanion sets the href).
      window.addEventListener('fyre:resolved', function () { requestAnimationFrame(triggerPrefetch); });
      new MutationObserver(triggerPrefetch).observe(companionHost, { attributes: true, attributeFilter: ['href'], subtree: true });
      triggerPrefetch(); // in case the resolver beat this script to a resolve

      // Delegated: the companion card is (re)rendered by fyre-resolve.js on each clean resolve, so
      // bind on the stable host, not the CTA node. The <a data-companion-cta href="/products/<rear>">
      // href IS the paired rear umbrella handle — read it and open in place instead of navigating.
      companionHost.addEventListener('click', function (e) {
        var cta = e.target.closest('[data-companion-cta]');
        if (!cta) return;
        e.preventDefault();
        var handle = (cta.getAttribute('href') || '').replace(/^\/products\//, '').split('?')[0].split('#')[0];
        if (!handle) return;
        self.openFor(handle);
      });
    },

    // Render the drawer instantly from the companion CARD's image/name (already on screen), then swap in
    // the prefetched Storefront identity + start the scoped resolver. No "Loading…" state.
    openFor: function (handle) {
      var self = this;
      var epoch = ++this._seq; // WHY: a newer open() or a close() invalidates every async continuation below
      this.currentHandle = handle;
      // bodyEl is REUSED across opens — hard-reset the resolved-variant attributes so the new session can
      // never briefly expose the prior session's sku/id (refresh() also clears them, but only once it runs).
      this.bodyEl.removeAttribute('data-cf-resolved-sku');
      this.bodyEl.removeAttribute('data-cf-resolved-id');
      var cardImg = companionHost.querySelector('[data-companion-img]');
      var cardName = companionHost.querySelector('[data-companion-name]');
      this.renderBody((cardImg && cardImg.src) || '', (cardName && cardName.textContent) || '');
      this.el.classList.add('pdp-cf--open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;

      var isStale = function () { return epoch !== self._seq; };
      var infoPromise = infoCache[handle] || prefetchRear(handle);
      if (!infoPromise) return this.fallbackNavigate(handle); // no Storefront config on this page -> navigate to the rear PDP instead
      infoPromise.then(function (info) {
        if (isStale() || !self.isOpen) return; // superseded by a newer open/close
        // rear isn't a configurable builder umbrella (no product / no builder collection) -> fail-closed
        // degrade to a plain navigate. Never after close (guard above).
        if (!info || !info.coll || !info.umbrella) return self.fallbackNavigate(handle);
        var nameEl = self.bodyEl.querySelector('[data-cf-name]');
        if (nameEl && info.title) nameEl.textContent = info.title;
        var imgEl = self.bodyEl.querySelector('[data-cf-img]');
        if (imgEl && info.image) { imgEl.src = sizedUrl(info.image, 720); imgEl.style.display = ''; }
        self._resolver = makeResolver(self.bodyEl, { coll: info.coll, umbrella: info.umbrella }, isStale, function (productId) {
          // ATC success → smooth hand-off: let ADDED ✓ read, slide THIS drawer out, THEN open
          // the mini-cart as it clears — never two right-drawers stacked. Dispatching 'atc:added' is what opens+
          // refreshes the cart (snippets/fyre-component-mini-cart.liquid); z 9999 > this drawer's 9998.
          var fireCart = function () { document.dispatchEvent(new CustomEvent('atc:added', { detail: { product_id: productId } })); };
          setTimeout(function () {
            if (epoch !== self._seq || !self.isOpen) { fireCart(); return; } // superseded during the dwell -> just reflect the add, don't touch the new session
            self.close();              // 300ms .pdp-cf__panel slide-out
            setTimeout(fireCart, 330); // cart enters right as the drawer finishes clearing
          }, 450);                     // ADDED ✓ dwell before the transition
        });
      }).catch(function () { if (!isStale() && self.isOpen) self.fallbackNavigate(handle); }); // WHY: never navigate after close
    },

    // Drawer body per the mockup: subtitle, image, name, four selects (COLOR/DIAMETER/DISC TYPE/BRAKE PAD),
    // message line, ADD-ON COST row + dark ATC. Selects start as disabled placeholders; the resolver populates.
    renderBody: function (imgSrc, name) {
      function selectRow(kind, attr, placeholder, ariaLabel) {
        return '<div class="pdp-cf__select-wrap" data-cf-row="' + kind + '">' +
          '<select class="pdp-cf__select" ' + attr + ' data-cf-placeholder="' + escAttr(placeholder) + '" aria-label="' + escAttr(ariaLabel) + '" disabled>' +
            '<option value="" disabled selected>' + escHtml(placeholder) + '</option>' +
          '</select>' +
          '<i class="pdp-cf__select-icon fa-solid fa-angle-down" aria-hidden="true"></i>' +
        '</div>';
      }
      this.bodyEl.innerHTML =
        '<p class="pdp-cf__sub">PLEASE CONFIRM CONFIGURATION FOR YOUR REAR KIT TO MATCH YOUR VEHICLE&rsquo;S FITMENT!</p>' +
        '<div class="pdp-cf__img-wrap">' +
          '<img class="pdp-cf__img" data-cf-img alt=""' + (imgSrc ? ' src="' + escAttr(imgSrc) + '"' : ' style="display:none"') + '>' +
        '</div>' +
        '<p class="pdp-cf__product-name" data-cf-name>' + escHtml(name) + '</p>' +
        '<div class="pdp-cf__selects">' +
          selectRow('Caliper Color', 'data-cf-opt="Caliper Color"', 'SELECT COLOR', 'Select color') +
          selectRow('Disc Diameter', 'data-cf-opt="Disc Diameter"', 'SELECT DIAMETER', 'Select diameter') +
          selectRow('Disc Type', 'data-cf-opt="Disc Type"', 'SELECT DISC TYPE', 'Select disc type') +
          selectRow('pad', 'data-cf-pad', 'SELECT BRAKE PAD', 'Select brake pad') +
        '</div>' +
        '<p class="pdp-cf__msg" data-cf-msg></p>' +
        '<div class="pdp-cf__footer">' +
          '<div class="pdp-cf__cost-row"><span>ADD-ON COST</span><span class="pdp-cf__cost-val" data-cf-cost>—</span></div>' +
          '<button type="button" class="pdp-cf__atc" data-cf-atc disabled>CHECKING…</button>' +
        '</div>';
    },

    // Fail-closed degrade: navigate to the rear PDP, so a missing Storefront config / unresolvable rear
    // lands on a real page instead of a broken empty configurator.
    fallbackNavigate: function (handle) { window.location.href = '/products/' + handle; },

    close: function () {
      if (!this.el) return;
      this._seq++; // WHY: shared epoch — close invalidates the in-flight open fetch AND the active resolver's async chain
      this.el.classList.remove('pdp-cf--open');
      this.isOpen = false;
      var self = this;
      // Only release the scroll lock if nothing re-opened in the interim (guards interleaved open/close).
      setTimeout(function () { if (!self.isOpen) document.body.style.overflow = ''; }, 300);
    }
  };

  FyreCompanionFlyout.init();
})();
